import React, { useState } from "react";
import { HiOutlineClock, HiOutlineCollection } from "react-icons/hi";
import { IoLocationOutline, IoClose } from "react-icons/io5";
import CustomButton from "../../components/common/CustomButton";
import {
  useCreateCheckoutSession,
  useCreateCheckoutSessionGuest,
  useGetUserBookingActiveAgreement,
} from "../../custom-hooks/queries";
import { toast } from "react-toastify";
import { useAuthStore } from "../../store/useAuthStore";

export default function ReviewBookingModal({
  isOpen,
  onClose,
  experience = {},
  selectedDate,
  // selectedTimeSlot,
  people = 1,
  basePrice = 0,
  priceType = "PerPerson",
  selectedAddOns = [],
  addOnsTotal = 0,
  totalPrice = 0,
  isProcessing = false,
  validationData,
  email,
  successMessage,
  specificStartTime,
}) {
  const review = validationData?.data || {};

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const token = useAuthStore.getState().token;
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  // 🔴 NEW – alternate flow states
  const [isConfirming, setIsConfirming] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [alternateSlots, setAlternateSlots] = useState([]);
  const [selectedAlternateSlot, setSelectedAlternateSlot] = useState(null);
  const [alternateSuggestions, setAlternateSuggestions] = useState([]);

  const quantityLabel = priceType === "PerPerson" ? "People" : "Groups";

  const quantityValue = people;

  const baseTotal = review?.baseAmount * quantityValue;

  // ✅ Hook ALWAYS called
  const { mutate: createCheckoutSession, isPending: isLoading } =
    useCreateCheckoutSession();

  const { mutate: createCheckoutSessionGuest, isPending: isLoadingGuest } =
    useCreateCheckoutSessionGuest();

  const { data: activeUserAgreement } = useGetUserBookingActiveAgreement();

  if (!isOpen) return null;

  const formatDisplayDate = (date) => {
    if (!date) return "-";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleProceedToPayment = () => {
    if (!agreeToTerms) {
      toast.error("Please agree to the User Agreement to continue.");
      return;
    }

    // if (!selectedTimeSlot) {
    //   toast.error("Please select a time slot");
    //   return;
    // }

    setIsConfirming(true);
    setCheckoutError(null);
    setAlternateSlots([]);
    setSelectedAlternateSlot(null);

    const payload = {
      experienceId: experience.experienceId,
      selectedDate: review?.selectedDate,
      selectedTimeSlot: !specificStartTime
        ? selectedAlternateSlot?.timeSlot || selectedTimeSlot
        : review?.selectedTimeSlot,
      // priceType: experience.priceType,
      nofPersons: review?.nofPersons || 0,
      addOnIds: review?.addOns.map((a) => a.addOnId),
      selectedStartTime: specificStartTime ? review?.selectedStartTime : null,
      // userEmail: email,
    };
    const payloadGuest = {
      experienceId: experience.experienceId,
      selectedDate: review?.selectedDate,
      selectedTimeSlot: !specificStartTime
        ? selectedAlternateSlot?.timeSlot || selectedTimeSlot
        : review?.selectedTimeSlot,
      priceType: experience.priceType,
      nofPersons: review?.nofPersons || 1,
      nofGroups: experience.priceType === "PerGroup" ? people : 0,
      userEmail: email,
      selectedStartTime: specificStartTime ? review?.selectedStartTime : null,
    };

    if (token) {
      createCheckoutSession(payload, {
        onSuccess: (res) => {
          if (res?.data?.checkoutUrl) {
            window.location.href = res.data.checkoutUrl;
          }
        },
        onError: (err) => {
          setIsConfirming(false);

          if (err?.response?.status === 409) {
            const data = err.response.data || {};

            setCheckoutError("Selected time is no longer available.");

            if (
              Array.isArray(data?.data.suggestions) &&
              data?.data.suggestions.length > 0
            ) {
              setAlternateSuggestions(data?.data?.suggestions);
            }

            return;
          }

          toast.error("Failed to create checkout session. Please try again.");
        },
      });
    } else {
      createCheckoutSessionGuest(payloadGuest, {
        onSuccess: (res) => {
          if (res?.data?.checkoutUrl) {
            window.location.href = res.data.checkoutUrl;
          }
        },
        onError: () => {
          toast.error("Failed to create checkout session. Please try again.");
        },
      });
    }
  };

  const baseSubtotal = review.nofPersons * review.unitPrice;

  const isNoSlotsAvailable =
    validationData?.status === 200 &&
    typeof validationData?.success_message === "string" &&
    validationData.success_message.toLowerCase() ===
      "no slots available for the selected date." &&
    validationData?.data?.isFullyBooked === true &&
    Array.isArray(validationData?.data?.availableSlots) &&
    validationData.data.availableSlots.length === 0;

  if (isNoSlotsAvailable) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* BACKDROP */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* MODAL */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            No Slots Available
          </h2>

          <p className="mt-3 text-sm text-gray-600 leading-relaxed">
            No slots are available for the selected date{" "}
            <span className="font-medium">
              ({validationData?.data?.selectedDate})
            </span>
            . Please try selecting a different date to continue your booking.
          </p>

          <div className="mt-6 flex justify-center">
            <CustomButton onClick={onClose} className="px-8">
              OK
            </CustomButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* MODAL */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[90vh] overflow-hidden">
        {/* CLOSE */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-full hover:bg-gray-100 z-10"
        >
          <IoClose size={22} />
        </button>

        {/* CONTENT */}
        <div className="p-4 sm:p-8 overflow-y-auto max-h-[90vh]">
          <h1 className="text-xl sm:text-[26px] font-semibold">
            Review Your Booking
          </h1>
          <h4 className="mt-5">
            Please review the details before pending to payment
          </h4>

          {/* EXPERIENCE INFO */}
          {checkoutError && (
            <div className="mb-4 mt-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {checkoutError}
            </div>
          )}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <img
              src={experience.mainImage}
              alt={experience.title}
              className="w-full h-[180px] sm:h-[200px] object-stretch object-center rounded-xl"
            />

            <div className="flex flex-col gap-3">
              <h2 className="text-lg sm:text-2xl font-semibold">
                {experience.title}
              </h2>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <IoLocationOutline />
                <span>{experience?.location?.fullAddress}</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiOutlineCollection />
                <span>{experience.category}</span>
              </div>
            </div>
          </div>

          {!specificStartTime && <div className="mt-6 border-t" />}

          {/* BOOKING SUMMARY */}
          <div
            className={`${specificStartTime ? "mt-6" : "mt-6"} space-y-3 text-sm sm:text-base`}
          >
            {!specificStartTime && review?.availableTimeSlots?.length > 0 && (
              <div className="flex justify-between">
                <span className="text-black font-semibold">Selected Date</span>
                <span className="font-medium">
                  <span>
                    {formatDisplayDate(review.selectedDate || selectedDate)}
                  </span>
                </span>
              </div>
            )}
            <div className="mt-6 border-t" />

            {!specificStartTime && review?.availableTimeSlots?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Available Time Slots
                </h4>

                <div className="flex flex-wrap gap-2">
                  {review?.availableTimeSlots.map((slot) => {
                    const isSelected = selectedTimeSlot === slot;
                    const isUnavailable =
                      checkoutError && selectedTimeSlot === slot;

                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => {
                          setSelectedTimeSlot(slot);
                          setSelectedAlternateSlot(null); // 🔥 reset alternate if user reselects
                          setCheckoutError(null);
                        }}
                        className={`
    px-4 py-2 rounded-full text-sm font-medium border transition
    ${
      isSelected
        ? "bg-[#1FADC6] text-white border-[#1FADC6]"
        : isUnavailable
          ? "bg-gray-200 text-gray-400 border-gray-300 line-through"
          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
    }
  `}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {specificStartTime && (
              <div className="mt-6 space-y-3 text-sm sm:text-base">
                <h3 className="text-base sm:text-lg font-semibold">
                  Booking Summary
                </h3>
                {/* Base Unit Price */}
                <div className="flex justify-between">
                  <span className="text-black font-semibold">
                    Selected Start Time
                  </span>
                  <span className="font-medium">
                    {review?.selectedStartTime}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black font-semibold">Booking Slot</span>
                  <span className="font-medium">
                    {review?.selectedTimeSlot}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black font-semibold">
                    Selected Date
                  </span>
                  <span className="text-black font-semibold">
                    {formatDisplayDate(review.selectedDate || selectedDate)}
                  </span>{" "}
                </div>
                {/* Quantity */}
                <div className="flex justify-between">
                  <span className="text-black font-semibold">
                    Duration (Minutes)
                  </span>
                  <span className="font-medium">{review?.durationMinutes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black font-semibold">Guest(s)</span>
                  <span className="font-medium">{people}</span>
                </div>
                {/* Base Subtotal */}
              </div>
            )}

            {alternateSuggestions.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Closest available times
                </h4>

                <div className="flex flex-wrap gap-2">
                  {alternateSuggestions.map((s, idx) => {
                    const isActive =
                      selectedAlternateSlot?.date === s.date &&
                      selectedAlternateSlot?.timeSlot === s.timeSlot;

                    return (
                      <button
                        key={`${s.date}-${s.timeSlot}-${idx}`}
                        type="button"
                        onClick={() => {
                          setSelectedAlternateSlot(s);
                          setSelectedTimeSlot(null);
                        }}
                        className={`
          px-4 py-2 rounded-full text-sm font-medium border transition
          ${
            isActive
              ? "bg-[#F97316] text-white border-[#F97316]"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
          }
        `}
                      >
                        {s.timeSlot}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 💰 PRICE SUMMARY */}
          <div className="mt-6 border-t" />

          {/* 💰 PRICE SUMMARY */}
          <div className="mt-6 space-y-3 text-sm sm:text-base">
            <h3 className="text-base sm:text-lg font-semibold">
              Price Summary
            </h3>
            {/* Base Unit Price */}
            <div className="flex justify-between">
              <span className="text-black font-semibold">Unit Price</span>
              <span className="font-medium">
                ${Number(review.unitPrice).toFixed(2)}
              </span>
            </div>
            {/* Quantity */}
            <div className="flex justify-between">
              <span className="text-black font-semibold">Guest(s)</span>
              <span className="font-medium">{review.nofPersons ?? people}</span>
            </div>
            {/* Base Subtotal */}
            <div className="flex justify-between pt-1 border-t">
              <span className="text-black font-semibold">Base Subtotal</span>
              <span className="font-medium">${baseSubtotal}</span>
            </div>
            {/* ADD-ONS */}
            {/* 🧩 ADD-ONS DETAILS */}
            {Array.isArray(review.addOns) && review.addOns.length > 0 && (
              <div className="pt-3 border-t space-y-2">
                <div className="text-sm font-semibold text-gray-900">
                  Selected Add-ons
                </div>

                {review.addOns.map((addon, idx) => (
                  <div
                    key={addon.addOnId || idx}
                    className="flex justify-between text-gray-700 text-sm"
                  >
                    <span>{addon.name}</span>
                    <span>${Number(addon.price).toFixed(2)}</span>
                  </div>
                ))}

                <div className="flex justify-between pt-2 border-t font-medium">
                  <span>Add-ons Subtotal</span>
                  <span>${Number(review.addOnsTotal || 0).toFixed(2)}</span>
                </div>
              </div>
            )}
            {/* FINAL TOTAL */}
            <div className="flex justify-between pt-4 border-t text-lg font-semibold">
              <span>Total</span>
              <span>
                ${Number(review.totalAmount ?? totalPrice).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="mt-6 border-t" />

          {/* ✅ AGREEMENT CHECKBOX */}
          <div className="mt-6 flex items-start">
            <input
              id="agreeToTerms"
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="h-4 w-4 mt-1 text-[#16BBD8] rounded border-gray-300"
            />

            <label
              htmlFor="agreeToTerms"
              className="ml-2 text-[12px] text-gray-800 font-medium mt-1"
            >
              I agree to the{" "}
              <a
                href={activeUserAgreement?.documentUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#1FADC6] hover:text-[#00C2C7]"
                onClick={(e) => e.stopPropagation()}
              >
                Toucan User Agreement & Liability Release
              </a>
            </label>
          </div>

          {/* ACTIONS */}
          <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3">
            <CustomButton
              variant="secondary"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              Cancel
            </CustomButton>

            <CustomButton
              onClick={handleProceedToPayment}
              disabled={
                isLoading ||
                isConfirming ||
                !agreeToTerms ||
                !(
                  selectedTimeSlot ||
                  selectedAlternateSlot ||
                  review?.selectedStartTime
                ) || // 👈 EITHER one required
                (alternateSuggestions.length > 0 && !selectedAlternateSlot)
              }
              loading={isLoading || isLoadingGuest}
              className={`w-full sm:w-auto ${
                !agreeToTerms ||
                !(
                  selectedTimeSlot ||
                  selectedAlternateSlot ||
                  review?.selectedStartTime
                )
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isConfirming
                ? "Processing..."
                : alternateSuggestions.length > 0
                  ? "Proceed with selected alternate"
                  : "Proceed to Payment"}
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
}
